/**
 * The draft as a table (a laptop, from 768 px): one row per requested line in triage
 * order, grouped as "Needs you", "Matched" and "Nothing to quote" (folded). The status
 * cell is the drill button of its row: the DS opens one detail row at a time under it,
 * and the detail row's mount tells the page which line a person looks at, so the mail
 * marks its words. Work closed: the action column is hidden.
 *
 * The grid is operated with the keys (`interaction="grid"`): the arrow keys walk the
 * cells, Enter on a cell confirms the row or opens its item search (the page takes the
 * key before the grid does, see `DraftLines`), and F2 steps into the control of a cell.
 *
 * The DS grid gives every column without a width 120 px in the fixed layout; the two
 * text columns are set back to auto with an important width so they share what the
 * fixed columns (584 px) leave. Filed as a DS gap (no flex column in `tableLayout="fixed"`).
 */

import { Button, DataGrid } from "@norican/react";
import { useEffect, useState } from "react";

import {
  DRAFT_COLUMN_IDS,
  type DraftCellContext,
  draftColumns,
  DraftLineExtras,
} from "@/features/case-review/draft-line-cells";
import { type DraftRow } from "@/features/case-review/draft-line-groups";
import { DRAFT_GROUPS, LONG_LIST_FOLD_AT, LONG_LIST_WORDS } from "@/lib/sales-flow";

/** The groups open on first render; "Nothing to quote" stays folded. */
const OPEN_GROUPS = [`group:${DRAFT_GROUPS.needsYou}`, `group:${DRAFT_GROUPS.matched}`];
const NOTHING_GROUP = `group:${DRAFT_GROUPS.nothingToQuote}`;

/**
 * The groups open on first render (hunt round 2, decision 4): "Needs you" always, and
 * "Matched" while it holds at most `LONG_LIST_FOLD_AT` rows. A longer group starts
 * folded with "Show all n lines"; every row stays in the draft.
 */
export function initialExpandedGroups(rows: readonly DraftRow[]): string[] {
  return OPEN_GROUPS.filter(
    (id) =>
      id === `group:${DRAFT_GROUPS.needsYou}` ||
      rows.filter((row) => `group:${row.group}` === id).length <= LONG_LIST_FOLD_AT,
  );
}

/** The folded groups over `LONG_LIST_FOLD_AT` rows, with their row counts. */
export function foldedLongGroups(
  rows: readonly DraftRow[],
  expanded: readonly string[],
): { id: string; n: number }[] {
  return [DRAFT_GROUPS.matched, DRAFT_GROUPS.nothingToQuote]
    .map((group) => ({
      id: `group:${group}`,
      n: rows.filter((row) => row.group === group).length,
    }))
    .filter((entry) => entry.n > LONG_LIST_FOLD_AT && !expanded.includes(entry.id));
}

/** The ids of the rows in "Nothing to quote", as one key for the comparison between renders. */
export function nothingToQuoteKey(rows: readonly DraftRow[]): string {
  return rows
    .filter((row) => row.group === DRAFT_GROUPS.nothingToQuote)
    .map((row) => row.line.id)
    .join("\n");
}

/**
 * The expanded ids after the rows changed: when a row landed in "Nothing to quote" (a
 * person pressed No item), the group opens so the row stays in sight with its Undo
 * (tester round 5, 12 Oct 2026: the row vanished into the folded group). A row that
 * left the group, or the first render, changes nothing.
 */
export function expandedAfterChange(
  expanded: readonly string[],
  before: string,
  after: string,
): string[] {
  const was = new Set(before.split("\n").filter(Boolean));
  const landed = after.split("\n").some((id) => id && !was.has(id));
  if (!landed || expanded.includes(NOTHING_GROUP)) {
    return [...expanded];
  }
  return [...expanded, NOTHING_GROUP];
}

/**
 * The detail row's content: tells the page on mount which line is open (the DS gives no
 * onOpen), so the mail marks the line's words. Filed with the DS gap: a controlled,
 * multi-open `renderCellDetail` would make this wrapper go.
 */
function DetailMount({
  lineId,
  onOpen,
  children,
}: {
  lineId: string;
  onOpen?: (lineId: string) => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    onOpen?.(lineId);
    // Once per open: the page marks the line when the detail row appears, not on every render.
  }, [lineId]);
  return <div data-line-detail-row={lineId}>{children}</div>;
}

export interface DraftTableProps {
  rows: readonly DraftRow[];
  ctx: DraftCellContext;
  /** A row's chevron opened its details: this is the line a person looks at. */
  onOpenDetails?: (lineId: string) => void;
  /** The line the page points at (the big button, a blocker sentence): its row is marked current. */
  focusLineId?: string | null;
}

export function DraftTable({ rows, ctx, onOpenDetails, focusLineId = null }: DraftTableProps) {
  const workOpen = (ctx.detail.workStatus ?? "open") === "open";
  // The expansion is controlled so the page can open "Nothing to quote" when a row
  // lands there; the person's own toggles are kept as the DS reports them.
  const [expanded, setExpanded] = useState<string[]>(() => initialExpandedGroups(rows));
  const nothingKey = nothingToQuoteKey(rows);
  const [seenNothingKey, setSeenNothingKey] = useState(nothingKey);
  if (nothingKey !== seenNothingKey) {
    setSeenNothingKey(nothingKey);
    setExpanded(expandedAfterChange(expanded, seenNothingKey, nothingKey));
  }
  const columns = draftColumns(ctx, (row) => (
    <DetailMount lineId={row.line.id} onOpen={onOpenDetails}>
      <DraftLineExtras row={row} ctx={ctx} />
    </DetailMount>
  ));
  const folded = foldedLongGroups(rows, expanded);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {folded.length > 0 && (
        <span className="flex flex-wrap items-center gap-2">
          {folded.map((group) => (
            <Button
              key={group.id}
              size="sm"
              variant="outline"
              tone="neutral"
              onClick={() => setExpanded([...expanded, group.id])}
              data-show-all-lines={group.id}
            >
              {LONG_LIST_WORDS.fold(group.n)}
            </Button>
          ))}
        </span>
      )}
      <DraftGrid
        columns={columns}
        rows={rows}
        expanded={expanded}
        setExpanded={setExpanded}
        workOpen={workOpen}
        focusLineId={focusLineId}
      />
    </div>
  );
}

/** The DS grid of the draft, grouped by "Needs you", "Matched" and "Nothing to quote". */
function DraftGrid({
  columns,
  rows,
  expanded,
  setExpanded,
  workOpen,
  focusLineId,
}: {
  columns: ReturnType<typeof draftColumns>;
  rows: readonly DraftRow[];
  expanded: string[];
  setExpanded: (ids: string[]) => void;
  workOpen: boolean;
  focusLineId: string | null;
}) {
  return (
    <DataGrid
      columns={columns}
      data={[...rows]}
      getRowId={(row) => row.line.id}
      groupBy={(row) => row.group}
      expandedIds={expanded}
      onExpandedChange={setExpanded}
      hiddenColumnIds={workOpen ? undefined : [DRAFT_COLUMN_IDS.action]}
      tableLayout="fixed"
      density="compact"
      // The draft is operated, not only read (ease round, 13 Oct 2026): the arrow keys
      // walk the cells, and the row keys of the page act on the row that holds the focus.
      interaction="grid"
      currentRowId={focusLineId}
      aria-label="Draft lines"
      className="[&_tr[data-row-highlight]>td]:bg-accent-subtle [&_col:nth-child(2)]:!w-auto [&_col:nth-child(3)]:!w-auto"
      data-draft-table
    />
  );
}
